import { readStorage, STORAGE_KEYS, writeStorage } from '../lib/storage';

/**
 * The signed-in session: tokens live in memory and in sessionStorage (per tab, gone when the tab
 * closes) — never in localStorage. Subscribers (React) are told about every change, so a refresh
 * failure anywhere signs the whole panel out.
 */
export interface Session {
  accessToken: string;
  refreshToken: string;
}

type Listener = (session: Session | null) => void;

function isSession(v: unknown): v is Session {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Session).accessToken === 'string' &&
    typeof (v as Session).refreshToken === 'string'
  );
}

/** Why the last session ended: the user signed out, or the API refused to refresh it. */
export type SessionEndReason = 'logout' | 'expired';

export class SessionStore {
  private session: Session | null;
  private readonly listeners = new Set<Listener>();
  /** Set when a session ends (shown once on the sign-in screen), cleared on sign-in. */
  endReason: SessionEndReason | null = null;

  constructor(private readonly persist = true) {
    this.session = persist ? SessionStore.load() : null;
  }

  private static load(): Session | null {
    const raw = readStorage('session', STORAGE_KEYS.session);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      return isSession(parsed)
        ? { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken }
        : null;
    } catch {
      return null;
    }
  }

  get(): Session | null {
    return this.session;
  }

  set(session: Session | null, reason: SessionEndReason = 'logout'): void {
    if (session === null && this.session === null) return;
    this.endReason = session ? null : reason;
    this.session = session;
    if (this.persist) {
      writeStorage('session', STORAGE_KEYS.session, session ? JSON.stringify(session) : null);
    }
    for (const l of [...this.listeners]) l(session);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
